import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspaceFilters } from "@presentation/hooks/useWorkspaceFilters";
import { useWorkspaceFilterStore, type WorkspaceFilterSelection } from "@presentation/stores/WorkspaceFilterStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { settingsUrl } from "@presentation/common/settingsRoutes";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import type { TeamProjectInventoryItem } from "@domain/schemas/workspace";

const scopeLabels = { deployment: "Deployment", team: "Team", project: "Project" } as const;

export const WorkspaceFilters: React.FC = () => {
  const filters = useWorkspaceFilters();
  const { pathname, search, hash } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const service = useInjection(ListTeamsService);
  const [menu, setMenu] = useState<"scope" | "team" | "project" | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);
  const [inventory, setInventory] = useState<{ teamId: string; projects: TeamProjectInventoryItem[] } | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scope, teamId, projectId, workspace, requiresSelection } = filters;
  const remember = useWorkspaceFilterStore((state) => state.remember);
  const setActiveTeamContext = useWorkspaceContextStore((state) => state.setActiveTeamContext);
  const menuTeamId = pendingTeamId ?? teamId;

  const targetUrl = (selection: WorkspaceFilterSelection) => {
    if (pathname === "/settings") {
      if (selection.scope === "deployment") return settingsUrl({ level: "deployment" }, params);
      return settingsUrl(selection.scope === "team" ? { level: "team", teamId: selection.teamId } : { level: "project", projectId: selection.projectId }, params);
    }
    const next = new URLSearchParams(params);
    next.set("scope", selection.scope);
    next.delete("teamId");
    next.delete("projectId");
    if (selection.scope !== "deployment" && selection.teamId) next.set("teamId", selection.teamId);
    if (selection.scope === "project" && selection.projectId) next.set("projectId", selection.projectId);
    return `${pathname}?${next}`;
  };

  useEffect(() => {
    if (workspace.status !== "loaded") return;
    if (!["/", "/projects", "/jobs", "/job-setup", "/teams", "/settings", "/about"].includes(pathname)) return;
    if (
      pathname === "/settings" &&
      filters.hasExplicitFilters &&
      (!["deployment", "team", "project"].includes(params.get("scope") ?? "") ||
        (params.get("scope") === "team" && !params.get("teamId")) ||
        (params.get("scope") === "project" && !params.get("projectId")))
    )
      return;
    if (pathname === "/settings" && params.get("scope") === "deployment" && scope !== "deployment") return;
    if (requiresSelection && ((scope === "project" && !projectId) || (scope === "team" && !teamId))) return;
    remember(pathname, { scope, teamId, projectId });
    if (scope === "project" && projectId) setActiveTeamContext(teamId, projectId);
    const target = targetUrl({ scope, teamId, projectId });
    if (target !== `${pathname}${search}`) navigate(`${target}${hash}`, { replace: true });
  }, [pathname, search, scope, teamId, projectId, workspace.status]);

  useEffect(() => {
    if (!menu) return;
    const close = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setMenu(null);
      setPendingTeamId(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      setMenu(null);
      setPendingTeamId(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [menu]);

  useEffect(() => {
    setSearchTerm("");
  }, [menu]);

  useEffect(() => {
    setMenu(null);
    setPendingTeamId(null);
  }, [pathname]);

  useEffect(() => {
    if (menu !== "project" || !menuTeamId) return;
    let isCancelled = false;
    setInventoryError(null);
    void service
      .getTeamProjectInventoryAsync(menuTeamId)
      .then((projects) => {
        if (!isCancelled) setInventory({ teamId: menuTeamId, projects });
      })
      .catch((error: unknown) => {
        console.error("Could not load team projects for the workspace filter", error);
        if (isCancelled) {
          return;
        }

        setInventory(null);
        setInventoryError("Could not load the team's project inventory. Showing your known projects.");
      });
    return () => {
      isCancelled = true;
    };
  }, [menu, menuTeamId, service]);

  const choose = (selection: WorkspaceFilterSelection) => {
    remember(pathname, selection);
    if (selection.scope === "project" && selection.projectId) setActiveTeamContext(selection.teamId, selection.projectId);
    setMenu(null);
    setPendingTeamId(null);
    navigate(`${targetUrl(selection)}${hash}`);
  };
  const projectsById = new Map(workspace.projects.filter((project) => !menuTeamId || project.teamId === menuTeamId).map((project) => [project.id, project]));
  const projectOptions: Array<{ id: string; name: string; teamId: string }> = [...projectsById.values()];
  if (inventory?.teamId === menuTeamId) {
    for (const project of inventory.projects) if (!projectsById.has(project.id)) projectOptions.push(project);
  }
  const teamsById = new Map(workspace.teams.map((team) => [team.id, { id: team.id, name: team.name }]));
  for (const project of workspace.projects) if (!teamsById.has(project.teamId)) teamsById.set(project.teamId, { id: project.teamId, name: project.teamId });
  const teamLabel = menuTeamId ? (teamsById.get(menuTeamId)?.name ?? "Selected team") : "All teams";
  const projectLabel = pendingTeamId === null && projectId ? (workspace.projects.find((project) => project.id === projectId)?.name ?? "Selected project") : "All projects";
  const toggle = (next: typeof menu) => {
    setPendingTeamId(null);
    setMenu((previous) => (previous === next ? null : next));
  };
  const renderControl = (kind: "scope" | "team" | "project", label: string, value: string) => (
    <div className="header-breadcrumb-project-switcher">
      <button
        type="button"
        className="header-breadcrumb-button header-breadcrumb-project-main"
        aria-label={`${label}: ${value}`}
        aria-haspopup="menu"
        aria-expanded={menu === kind}
        onClick={() => toggle(kind)}
      >
        <span className="header-breadcrumb-key">{label}</span>
        {value}
      </button>
      <button
        type="button"
        className="header-breadcrumb-project-toggle"
        title={`Switch ${kind}`}
        aria-label={`Switch ${kind}`}
        aria-haspopup="menu"
        aria-expanded={menu === kind}
        onClick={() => toggle(kind)}
      >
        ▾
      </button>
      {menu === kind && (
        <div className="header-breadcrumb-project-menu" role="menu" aria-label={{ scope: "Scope", team: "Teams", project: "Team projects" }[kind]}>
          {kind !== "scope" && (
            <input type="search" className="header-breadcrumb-project-search" placeholder={`Search ${kind}s`} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
          )}
          {kind === "scope" &&
            filters.allowedScopes.map((level) => (
              <button
                type="button"
                role="menuitem"
                className="header-breadcrumb-project-menu-item"
                key={level}
                onClick={() => {
                  const nextTeamId = filters.lastTeamId ?? workspace.activeTeamId;
                  const nextProject = filters.lastProjectSelection ?? { teamId: workspace.activeTeamId, projectId: workspace.activeProjectId };
                  choose({
                    scope: level,
                    teamId: level === "project" ? nextProject.teamId : nextTeamId,
                    projectId: level === "project" ? nextProject.projectId : "",
                  });
                }}
              >
                {scopeLabels[level]}
              </button>
            ))}
          {kind === "team" &&
            [...(requiresSelection ? [] : [{ id: "", name: "All teams" }]), ...teamsById.values()]
              .filter((team) => team.name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
              .map((team) => (
                <button
                  type="button"
                  role="menuitem"
                  className="header-breadcrumb-project-menu-item"
                  key={team.id}
                  onClick={() => {
                    if (scope === "project") {
                      setPendingTeamId(team.id);
                      setMenu("project");
                    } else choose({ scope, teamId: team.id, projectId: "" });
                  }}
                >
                  {team.name}
                </button>
              ))}
          {kind === "project" && (
            <>
              {inventoryError && <p role="alert">{inventoryError}</p>}
              {[...(requiresSelection ? [] : [{ id: "", name: "All projects", teamId: menuTeamId }]), ...projectOptions]
                .filter((project) => project.name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
                .toSorted((left, right) => left.name.localeCompare(right.name))
                .map((project) => (
                  <button
                    type="button"
                    role="menuitem"
                    className="header-breadcrumb-project-menu-item"
                    key={project.id}
                    onClick={() => choose({ scope: "project", teamId: project.teamId, projectId: project.id })}
                  >
                    {project.name}
                  </button>
                ))}
              {requiresSelection && projectOptions.length === 0 && <p role="status">No projects available for this team.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
  return (
    <div className={`header-subtitle header-breadcrumbs${menu ? " has-open-menu" : ""}`} ref={containerRef}>
      {renderControl("scope", "Scope", scopeLabels[scope])}
      {scope !== "deployment" && (
        <>
          <span className="header-breadcrumb-separator">›</span>
          {renderControl("team", "Team", teamLabel)}
        </>
      )}
      {scope === "project" && (
        <>
          <span className="header-breadcrumb-separator">›</span>
          {renderControl("project", "Project", projectLabel)}
        </>
      )}
    </div>
  );
};
